"use client";

import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Mail, Phone, MapPin } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function ContactFormDetails() {
  return <section className="relative bg-[hsl(210,15%,15%)] text-[hsl(0,0%,100%)] py-20 px-4 md:px-8">
      <div className="container mx-auto max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <FormProvider className="w-full" formId="contact-form-hero" sectionName="contact-form-details" defaultValues={{
          name: "",
          email: "",
          phone: "",
          message: ""
        }} validationRules={{
          name: {
            required: true,
            minLength: 2,
            maxLength: 50
          },
          email: {
            required: true,
            pattern: "email"
          },
          phone: {
            required: true,
            pattern: "phone"
          },
          message: {
            required: true,
            minLength: 10
          }
        }} submitText="Send Message" successMessage="Thank you! We'll get back to you soon.">
            <ContactFormCard />
          </FormProvider>

          <ContactDetailsCard />
        </div>
      </div>
    </section>;
}
function ContactFormCard() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  const {
    ref: cardRef,
    isVisible: cardVisible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.2
  });
  if (isSubmitted && !submitError) {
    return <Card ref={cardRef} className="bg-[hsl(0,0%,100%)] text-[hsl(0,0%,0%)] shadow-[0_4px_16px_rgb(0_0_0_/_0.12),0_2px_6px_rgb(0_0_0_/_0.08)] transition-all duration-[250ms] ease-[cubic-bezier(0.22,1,0.36,1)]">
        <CardContent className="p-8">
          <div className="text-center space-y-4">
            <div className="w-16 h-16 bg-[hsl(215,80%,48%)] rounded-full flex items-center justify-center mx-auto">
              <Mail className="w-8 h-8 text-white" />
            </div>
            <h3 className="text-2xl font-semibold text-[hsl(215,80%,48%)]">
              Message Sent!
            </h3>
            <p className="text-[hsl(0,0%,30%)]">{successMessage}</p>
            <Button onClick={resetForm} variant="outline" className="mt-4 border-[hsl(0,0%,80%)] hover:bg-[hsl(0,0%,95%)]">
              Send Another Message
            </Button>
          </div>
        </CardContent>
      </Card>;
  }
  return <Card ref={cardRef} className={`bg-[hsl(0,0%,100%)] text-[hsl(0,0%,0%)] shadow-[0_4px_16px_rgb(0_0_0_/_0.12),0_2px_6px_rgb(0_0_0_/_0.08)] hover:shadow-[0_8px_32px_rgb(0_0_0_/_0.16),0_4px_12px_rgb(0_0_0_/_0.08)] hover:-translate-y-1 transition-all duration-[250ms] ease-[cubic-bezier(0.22,1,0.36,1)] ${cardVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
    transitionDelay: cardVisible ? "0ms" : "0ms"
  }}>
      <CardContent className="p-8">
        <h2 className="text-3xl font-bold text-[hsl(215,80%,48%)] mb-2 font-display">
          Get in Touch
        </h2>
        <p className="text-[hsl(0,0%,30%)] mb-6">
          Fill out the form below and we'll respond within 24 hours.
        </p>

        <Form onSubmit={handleSubmit}>
          <div className="space-y-5">
            <FormField control={control} name="name" render={({
            field
          }) => <FormItem className={`${cardVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"} transition-all duration-[250ms] ease-[cubic-bezier(0.25,0.46,0.45,0.94)]`} style={{
            transitionDelay: cardVisible ? "100ms" : "0ms"
          }}>
                  <FormLabel className="text-[hsl(0,0%,0%)] font-medium">
                    Name
                  </FormLabel>
                  <FormControl>
                    <Input placeholder="Your Name" {...field} className="border-[hsl(0,0%,80%)] bg-white focus-visible:border-[hsl(215,80%,48%)] focus-visible:ring-[hsl(215,80%,48%)]/50" />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />

            <FormField control={control} name="email" render={({
            field
          }) => <FormItem className={`${cardVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"} transition-all duration-[250ms] ease-[cubic-bezier(0.25,0.46,0.45,0.94)]`} style={{
            transitionDelay: cardVisible ? "200ms" : "0ms"
          }}>
                  <FormLabel className="text-[hsl(0,0%,0%)] font-medium">
                    Email
                  </FormLabel>
                  <FormControl>
                    <Input type="email" placeholder="you@example.com" {...field} className="border-[hsl(0,0%,80%)] bg-white focus-visible:border-[hsl(215,80%,48%)] focus-visible:ring-[hsl(215,80%,48%)]/50" />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />

            <FormField control={control} name="phone" render={({
            field
          }) => <FormItem className={`${cardVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"} transition-all duration-[250ms] ease-[cubic-bezier(0.25,0.46,0.45,0.94)]`} style={{
            transitionDelay: cardVisible ? "300ms" : "0ms"
          }}>
                  <FormLabel className="text-[hsl(0,0%,0%)] font-medium">
                    Phone
                  </FormLabel>
                  <FormControl>
                    <Input type="tel" placeholder="(555) 123-4567" {...field} className="border-[hsl(0,0%,80%)] bg-white focus-visible:border-[hsl(215,80%,48%)] focus-visible:ring-[hsl(215,80%,48%)]/50" />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />

            <FormField control={control} name="message" render={({
            field
          }) => <FormItem className={`${cardVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"} transition-all duration-[250ms] ease-[cubic-bezier(0.25,0.46,0.45,0.94)]`} style={{
            transitionDelay: cardVisible ? "400ms" : "0ms"
          }}>
                  <FormLabel className="text-[hsl(0,0%,0%)] font-medium">
                    Message
                  </FormLabel>
                  <FormControl>
                    <Textarea placeholder="Tell us about your project..." className="min-h-32 border-[hsl(0,0%,80%)] bg-white focus-visible:border-[hsl(215,80%,48%)] focus-visible:ring-[hsl(215,80%,48%)]/50 resize-none" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />

            {submitError && <div className={`p-4 bg-red-50 border border-red-200 rounded-lg ${cardVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"} transition-all duration-[250ms] ease-[cubic-bezier(0.25,0.46,0.45,0.94)]`} style={{
            transitionDelay: cardVisible ? "500ms" : "0ms"
          }}>
                <p className="text-red-800 text-sm">{submitError}</p>
              </div>}

            <div className={`${cardVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"} transition-all duration-[250ms] ease-[cubic-bezier(0.25,0.46,0.45,0.94)]`} style={{
            transitionDelay: cardVisible ? "500ms" : "0ms"
          }}>
              <Button type="submit" disabled={isSubmitting} className="w-full bg-[hsl(40,90%,55%)] text-white hover:bg-[hsl(40,88%,60%)] active:bg-[hsl(40,88%,50%)] hover:scale-[1.02] transition-all duration-[150ms] ease-[cubic-bezier(0.34,1.56,0.64,1)] shadow-[0_2px_8px_rgb(0_0_0_/_0.04),0_1px_3px_rgb(0_0_0_/_0.08)] hover:shadow-[0_4px_16px_rgb(0_0_0_/_0.12),0_2px_6px_rgb(0_0_0_/_0.08)] relative overflow-hidden">
                {isSubmitting ? <>
                    <span className="animate-spin mr-2"></span>
                    Sending...
                  </> : "Send Message"}
              </Button>
            </div>
          </div>
        </Form>
      </CardContent>
    </Card>;
}
function ContactDetailsCard() {
  const {
    ref: cardRef,
    isVisible: cardVisible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.2
  });
  return <Card ref={cardRef} className={`bg-[hsl(0,0%,100%)] text-[hsl(0,0%,0%)] shadow-[0_4px_16px_rgb(0_0_0_/_0.12),0_2px_6px_rgb(0_0_0_/_0.08)] hover:shadow-[0_8px_32px_rgb(0_0_0_/_0.16),0_4px_12px_rgb(0_0_0_/_0.08)] hover:-translate-y-1 transition-all duration-[250ms] ease-[cubic-bezier(0.22,1,0.36,1)] ${cardVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
    transitionDelay: cardVisible ? "200ms" : "0ms"
  }}>
      <CardContent className="p-8">
        <h2 className="text-3xl font-bold text-[hsl(215,80%,48%)] mb-2 font-display">
          Contact Information
        </h2>
        <p className="text-[hsl(0,0%,30%)] mb-8">
          Reach out to us directly through any of these channels.
        </p>

        <div className="space-y-6 mb-8">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-[hsl(215,80%,95%)] rounded-lg flex items-center justify-center shrink-0">
              <MapPin className="w-6 h-6 text-[hsl(215,80%,48%)]" />
            </div>
            <div>
              <h3 className="font-semibold text-[hsl(0,0%,0%)] mb-1">
                Address
              </h3>
              <p className="text-[hsl(0,0%,30%)]">2265 East Foothill Blvd, Pasadena, CA 91107</p>
            </div>
          </div>

          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-[hsl(180,70%,95%)] rounded-lg flex items-center justify-center shrink-0">
              <Phone className="w-6 h-6 text-[hsl(180,70%,53%)]" />
            </div>
            <div>
              <h3 className="font-semibold text-[hsl(0,0%,0%)] mb-1">Phone</h3>
              <p className="text-[hsl(0,0%,30%)]">310-801-6662</p>
            </div>
          </div>

          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-[hsl(40,90%,95%)] rounded-lg flex items-center justify-center shrink-0">
              <Mail className="w-6 h-6 text-[hsl(40,90%,55%)]" />
            </div>
            <div>
              <h3 className="font-semibold text-[hsl(0,0%,0%)] mb-1">Email</h3>
              <p className="text-[hsl(0,0%,30%)]">askus@flexrnd.com</p>
            </div>
          </div>
        </div>

        <div className="relative w-full h-64 bg-[hsl(0,0%,95%)] rounded-lg overflow-hidden hover:translate-y-[-4px] transition-all duration-[250ms] ease-[cubic-bezier(0.22,1,0.36,1)] shadow-[0_2px_8px_rgb(0_0_0_/_0.04),0_1px_3px_rgb(0_0_0_/_0.08)]">
          <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1475438/img/flex_dps_office_location_map.png" alt="Flex DPS Office Location Map" className="w-full h-full object-cover" />
        </div>
      </CardContent>
    </Card>;
}
export default ContactFormDetails;